import { createSelector, createSlice } from '@reduxjs/toolkit';

const initialState = {
  isLoading: false,
  isActive: false,
  isAdded: false,
  id: 0,
  uid: 0,
  sid: 0,
  profileId: 0,
  blockContent: {
    type: 'Statistics',
    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
    },
    items: [],
  },
  blockPreview: {
    isActive: false,
    view: 'compact',
    thumbnail: null,
    title: '',
    description: '',
    button: '',
    label: '',
    timeStart: null,
  },
  blockDesign: {
    containerDesign: {
      headingTitle: '',
      headingImage: '',
      headingTextAlign: 'left',
      headingDescription: '',
      headingDivider: '',
      sectionIsBackground: false,
      sectionTextColor: '#000000',
      sectionShadow: 'soft',
      sectionShadowColor: '#000',
      sectionShadowBorderColor: '#ECECF0',
      sectionBorderColor: '#ECECF0',
      sectionShape: 'round',
      sectionOpacity: 0,
      sectionBackgroundColor: '#ffffff',
      sectionIsFullWidth: false,
      sectionPadding: 20,
      sectionMargin: 12,
      sectionBackgroundType: 1,
      sectionBackgroundImage: '',
      sectionGradientColor1: '#B3F7EF',
      sectionGradientColor2: '#F7D6E0',
      sectionGradientDirection: 0,
      sectionGradientOffset: 0,
      sectionBorder: 'none',
    },

    itemDesign: {
      shadow: 'none',
      shadowColor: '#000',
      border: 'none',
      borderColor: '#ECECF0',
      shape: 'round',
      opacity: 0,
      textColor: '#000',
      backgroundColor: '#ffffff',
    },
    layout: '1',
    view: 'feed',
    itemFields: 'title, subtitle, description',
    itemTextAlign: 'right',
    itemTitleSize: '24',
    itemColumns: 2,
    previewDesign: {},
  },
};
export const statsBlockSlice = createSlice({
  name: 'stat',
  initialState,
  reducers: {
    // Core states
    statsUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    statsUpdateIsAdded: (state, action) => {
      state.isAdded = action.payload;
    },
    statsUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },

    // store full block
    statsStore: (state, action) => {
      const payload = action.payload ?? {};
      const { id, isActive, sid, uid, profileId, data, preview } = payload;
      const block = data ?? {};
      const { blockContent, blockDesign } = block;
      const { itemDesign, containerDesign, layout, view, itemFields, itemTextAlign, itemTitleSize, itemColumns, previewDesign } = blockDesign;
      state.isLoading = false;
      state.isActive = isActive;
      state.id = id ?? state.id;
      state.uid = uid ?? state.uid;
      state.sid = sid ?? state.sid;
      state.profileId = profileId ?? state.profileId;

      state.blockContent = {
        items: blockContent.items ?? [],
        heading: {
          title: blockContent.heading?.title ?? '',
          description: blockContent.heading?.description ?? '',
          textAlign: blockContent.heading?.textAlign ?? 'left',
          image: blockContent.heading?.image ?? '',
          divider: blockContent.heading?.divider ?? '',
          position: blockContent.heading?.position ?? '',
        },
      };
      state.blockDesign.itemDesign = itemDesign ?? {};
      state.blockDesign.containerDesign = containerDesign ?? {};
      state.blockDesign.layout = layout ?? '1';
      state.blockDesign.view = view ?? 'feed';
      state.blockDesign.itemFields = itemFields ?? 'title, subtitle, description';
      state.blockDesign.itemTextAlign = itemTextAlign ?? 'left';
      state.blockDesign.itemTitleSize = itemTitleSize ?? '24px';
      // Null on every block created before the column count was wired through — those render as
      // the 2-up grid they always were, so the fallback has to be 2 and not "unset".
      state.blockDesign.itemColumns = itemColumns ?? 2;
      state.blockDesign.previewDesign = previewDesign ?? {};
      state.blockPreview = {
        isActive: preview?.isActive || false,
        thumbnail: preview?.thumbnail ?? null,
        view: preview?.view ?? 'compact',
        title: preview?.title ?? '',
        description: preview?.description ?? '',
        button: preview?.button ?? '',
        label: preview?.label ?? '',
        timeStart: preview?.timeStart ?? null,
      };
    },
    // clear stats block
    statsClearBlock: () => initialState,

    // update stats block
    statsUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },

    // update block heading
    statsUpdateHeading: (state, action) => {
      const updatedHeading = action.payload;
      state.blockContent.heading = {
        ...state.blockContent.heading,
        ...updatedHeading,
      };
    },

    // stats item adding
    statsAddItem: (state, action) => {
      const newStatsItem = action.payload;
      if (!state.blockContent) {
        state.blockContent = { items: [] };
      }
      if (!Array.isArray(state.blockContent.items)) {
        state.blockContent.items = [];
      }
      state.blockContent.items.push(newStatsItem);
    },

    // single stats item update
    statsUpdateItem: (state, action) => {
      const updatedStatsItem = action.payload;
      const index = state.blockContent.items.findIndex((f) => f.id === updatedStatsItem.id);
      if (index !== -1) {
        state.blockContent.items[index] = { updatedStatsItem };
      }
    },

    // single file remove
    statsRemoveItem: (state, action) => {
      const statsItemId = action.payload;
      state.blockContent.items = state.blockContent.items.filter((f) => f.id !== statsItemId);
    },

    // update design
    statsUpdateBlockDesign: (state, action) => {
      const { itemDesign, containerDesign, layout, previewDesign } = action.payload;
      if (itemDesign) state.blockDesign.itemDesign = itemDesign;
      if (containerDesign) state.blockDesign.containerDesign = containerDesign;
      if (layout) state.blockDesign.layout = layout;
      if (previewDesign) state.blockDesign.previewDesign = previewDesign;
    },
    statsUpdatePreviewActive: (state, action) => {
      state.blockPreview.isActive = action.payload;
    },
    statsUpdateBlockPreviewView: (state, action) => {
      state.blockPreview.view = action.payload;
    },
    statsUpdateBlockPreviewContent: (state, action) => {
      const { field, value } = action.payload;
      state.blockPreview[field] = value;
    },
    statsUpdateBlockPreviewDesign: (state, action) => {
      const { field, value } = action.payload;
      state.blockDesign.previewDesign[field] = value;
    },
    // update item design layout
    statsUpdateLayout: (state, action) => {
      const updatedLayout = action.payload;
      state.blockDesign.layout = updatedLayout;
    },
    // update item design view
    statsUpdateView: (state, action) => {
      const updatedView = action.payload;
      state.blockDesign.view = updatedView;
    },
    statsUpdateItemFields: (state, action) => {
      const updatedItemFields = action.payload;
      state.blockDesign.itemFields = updatedItemFields;
    },
    statsUpdateItemTextAlign: (state, action) => {
      const updatedTextAlign = action.payload;
      state.blockDesign.itemTextAlign = updatedTextAlign;
    },
    statsUpdateItemTitleSize: (state, action) => {
      const updatedTitleSize = action.payload;
      state.blockDesign.itemTitleSize = updatedTitleSize;
    },
    statsUpdateItemColumns: (state, action) => {
      const updatedColumns = action.payload;
      state.blockDesign.itemColumns = updatedColumns;
    },
  },
});

export const {
  statsUpdateIsLoading,
  statsUpdateIsAdded,
  statsUpdateIsActive,
  statsStore,
  statsClearBlock,
  statsUpdateBlockContent,
  statsUpdateHeading,
  statsAddItem,
  statsUpdateItem,
  statsRemoveItem,
  statsUpdateBlockDesign,
  statsUpdateLayout,
  statsUpdateView,
  statsUpdateItemFields,
  statsUpdateItemTextAlign,
  statsUpdateItemTitleSize,
  statsUpdateItemColumns,
  statsUpdatePreviewActive,
  statsUpdateBlockPreviewView,
  statsUpdateBlockPreviewContent,
  statsUpdateBlockPreviewDesign,
} = statsBlockSlice.actions;

//  Selectors
export const selectStatsIsLoading = (state) => state.stat.isLoading;
export const selectStatsIsAdded = (state) => state.stat.isAdded;
export const selectStatsIsActive = (state) => state.stat.isActive;
export const selectStatsId = (state) => state.stat.id;
export const selectStatsUID = (state) => state.stat.uid;
export const selectStatsSID = (state) => state.stat.sid;
export const selectProfileId = (state) => state.stat.profileId;
export const selectStatsBlockContent = (state) => state.stat.blockContent;
export const selectStatsBlockHeading = (state) => state.stat.blockContent.heading;
export const selectStatsList = createSelector([selectStatsBlockContent], (blockContent) => blockContent?.items ?? []);
export const selectStatsBlockDesign = (state) => state.stat.blockDesign;
export const selectStatsItemDesign = (state) => state.stat.blockDesign.itemDesign;
export const selectStatsContainerDesign = (state) => state.stat.blockDesign.containerDesign;
export const selectStatsContainerLayout = (state) => state.stat.blockDesign.layout;
export const selectStatsContainerView = (state) => state.stat.blockDesign.view;
export const selectItemFields = (state) => state.stat.blockDesign.itemFields;
export const selectItemTextAlign = (state) => state.stat.blockDesign.itemTextAlign;
export const selectItemTitleSize = (state) => state.stat.blockDesign.itemTitleSize;
export const selectItemColumns = (state) => state.stat.blockDesign.itemColumns;
export const selectStatsPreviewContent = (state) => state.stat.blockPreview;
export const selectStatsPreviewDesign = (state) => state.stat.blockDesign.previewDesign;
export const selectStatsPreviewActive = (state) => state.stat.blockPreview.isActive;
export const selectStatsPreviewView = (state) => state.stat.blockPreview.view;
export default statsBlockSlice.reducer;
